import axios, { AxiosRequestConfig } from "axios";

type APIErrorData = { errors: string[], error?: string };

export interface Pagination {
  limit: number;
  offset: number;
  total_count: number
}

let requestRetryCount = 0;
let isRetrying = false;
export const createAccessTokenInterceptor = (getAccessTokenSilently, setHasFetchedProfile, logout) => {
  axios.interceptors.response.use(response => response,
    async (error) => {
      if (isRetrying) return;

      if (error.response?.status === 401) {
        isRetrying = true;
        const token = await getAccessTokenSilently();
        if (token && requestRetryCount === 0) {
          // retry the request with the new token and increment the count
          requestRetryCount++;
          error.config.headers.authorization = `Bearer ${token}`;
          const retriedRequest = await axios.request(error.config);
          // we want to make sure that the whole app gets a new token, so we'll trigger a universal token request here.
          // this is weird, but not sure what else to do since we need to also retry the request with a new token here and
          // cannot get the new token from the useUser update.
          // could be a good reason to make the doRequest system react hook based instead.
          if (retriedRequest.status !== 401) {
            setHasFetchedProfile(false);
            requestRetryCount = 0;
            isRetrying = false;
          }

          return retriedRequest;
        }
        if (requestRetryCount > 0) {
          requestRetryCount = 0;
          // we have refreshed the token and retried but something is very wrong, log the user out.
          // probably not a great user experience without a UX error. This should really never happen,
          // we could add a 'you have been logged out due to inactivity and security purposes' or something but not sure how to do that in the Auth0 ecosystem.
          logout({ logoutParams: { returnTo: window.location.origin }});
        }
      } else {
        throw error;
      }
    }
  )
}

// Helper function to make requests to the API
const doRequest = async <T>(
  method: 'get' | 'post' | 'put' | 'patch' | 'delete',
  url: string,
  accessToken: string | undefined,
  defaultError: string = "Request failed",
  data?: unknown,
  contentType: string = "application/json",
  concatErrors: boolean = true
): Promise<{ data?: T; pagination?: Pagination; error?: string, status?: number, errors?: string[] }> => {
  const options: AxiosRequestConfig = {
    headers: {
      authorization: `Bearer ${accessToken}`,
      'Content-Type': contentType,
      referrerPolicy: 'strict-origin-when-cross-origin',
    }
  };

  try {
    const path = `${import.meta.env.VITE_API_SERVER_URL}/api/v2${url}`;
    const response = await axios.request<{ data: T, pagination?: Pagination, errors?: string[] }>({
      method,
      url: path,
      data,
      ...options
    });

    if (response.status >= 200 && response.status < 300) {
      // reset requestRetryCount on successful response in case we were in a retry state
      requestRetryCount = 0;
      
      // Handle 207 Multi-Status responses that may contain both data and errors
      if (response.status === 207) {
        return {
          status: response.status,
          ...(response.data?.data && { data: response.data.data }),
          ...(response.data?.errors && { errors: response.data.errors })
        };
      }
      
      // Handle normal success responses
      if (response.data?.pagination) {
        return { data: response.data.data, pagination: response.data.pagination }
      }
      if (response.data) {
        return { data: response.data.data }
      }
      return { status: response.status };
    } else {
      return { error: `Request failed with status ${response.status}.`};
    }
  } catch (error) {
    if (axios.isAxiosError<APIErrorData>(error) && error.response) {
      if (error.response.data?.errors) {
        if (concatErrors) {
          return { error: error.response.data.errors.join(' ') }
        } else {
          return { errors: error.response.data.errors }
        }
      } else if(error.response.data?.error) {
        return { error: error.response.data.error }
      }
    }
    if (error instanceof Error) {
      return { error: error.message, status: 500 };
    }
    return { error: defaultError };
  }
};

export const getReq = async <T>(
  url: string,
  accessToken: string | undefined,
  defaultError: string = "Failed to fetch",
) => doRequest<T>('get', url, accessToken, defaultError);

export const postReq = async <T>(
  url: string,
  accessToken: string | undefined,
  data: unknown,
  defaultError: string = "Failed to post",
  contentType?: string,
  concatErrors?: boolean,
) => doRequest<T>('post', url, accessToken, defaultError, data, contentType, concatErrors);

export const putReq = async <T>(
  url: string,
  accessToken: string,
  data: unknown,
  defaultError: string = "Failed to update",
) => doRequest<T>('put', url, accessToken, defaultError, data);

export const patchReq = async <T>(
  url: string,
  accessToken: string,
  data: unknown,
  defaultError: string = "Failed to patch",
) => doRequest<T>('patch', url, accessToken, defaultError, data);

export const deleteReq = async <T>(
  url: string,
  accessToken: string,
  defaultError: string = "Failed to delete",
) => doRequest<T>('delete', url, accessToken, defaultError);

/** One error from the web API. `attribute` names the field, when the error is about one. */
export type WebApiError = {
  type: string;
  attribute?: string;
  code: string;
  message: string;
};

export type WebApiResult<T> = {
  data?: T;
  pagination?: Pagination;
  /** Every error message joined, ready to show. */
  error?: string;
  errors?: WebApiError[];
};

// Requests to the web API (/api/web/v1). It's separate from doRequest because
// the base path differs and its errors are objects rather than strings.
const doWebRequest = async <T>(
  method: 'get' | 'post' | 'patch' | 'delete',
  url: string,
  accessToken: string,
  data?: unknown,
): Promise<WebApiResult<T>> => {
  try {
    const response = await axios.request<{ data?: T; pagination?: Pagination }>({
      method,
      url: `${import.meta.env.VITE_API_SERVER_URL}/api/web/v1${url}`,
      data,
      headers: {
        authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });
    // reset requestRetryCount on successful response in case we were in a retry state
    requestRetryCount = 0;
    // A 204 (a delete) has no body
    return { data: response.data?.data, pagination: response.data?.pagination };
  } catch (error) {
    let result: WebApiResult<T> = { error: 'Something went wrong. Please try again.' };
    if (axios.isAxiosError<{ errors?: WebApiError[] }>(error) && error.response?.data?.errors?.length) {
      const errors = error.response.data.errors;
      result = { error: errors.map((apiError) => apiError.message).join(' '), errors };
    }
    return result;
  }
};

export const webGetReq = async <T>(url: string, accessToken: string) =>
  doWebRequest<T>('get', url, accessToken);

export const webPostReq = async <T>(url: string, accessToken: string, data: unknown) =>
  doWebRequest<T>('post', url, accessToken, data);

export const webPatchReq = async <T>(url: string, accessToken: string, data: unknown) =>
  doWebRequest<T>('patch', url, accessToken, data);

export const webDeleteReq = async (url: string, accessToken: string) =>
  doWebRequest<never>('delete', url, accessToken);

// Fetches a file from the v2 API, or from the web API (/api/web/v1) when
// apiName is 'web'
export const fetchFileBlob = async (
  url: string,
  accessToken: string,
  apiName: 'v2' | 'web' = 'v2'
): Promise<{ blob: Blob; filename: string, headers: any }> => {
  const apiPath = apiName === 'web' ? '/api/web/v1' : '/api/v2';
  const response = await axios.request({
    url: `${import.meta.env.VITE_API_SERVER_URL}${apiPath}${url}`,
    method: 'GET',
    responseType: 'blob',
    headers: {
      authorization: `Bearer ${accessToken}`,
      'Cache-Control': 'no-cache'
    }
  });
  const disposition = response.headers['content-disposition'];
  const filename = disposition?.match(/filename="([^"]+)"/)?.[1] ?? 'download';
  return { blob: response.data, filename, headers: response.headers };
};

export const triggerBlobDownload = (blob: Blob, filename: string): void => {
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
};

export const downloadFile = async (url: string, accessToken: string) => {
  const { blob, filename } = await fetchFileBlob(url, accessToken);
  triggerBlobDownload(blob, filename);
};

export const webDownloadFile = async (url: string, accessToken: string) => {
  const { blob, filename } = await fetchFileBlob(url, accessToken, 'web');
  triggerBlobDownload(blob, filename);
};
